import {
  CdkVirtualForOf,
  CdkVirtualScrollViewport,
  VIRTUAL_SCROLL_STRATEGY,
  VirtualScrollStrategy,
} from "@angular/cdk/scrolling";
import {
  afterRenderEffect,
  AfterViewInit,
  computed,
  contentChild,
  Directive,
  effect,
  forwardRef,
  inject,
  input,
  isSignal,
  NgModule,
  NgZone,
  signal,
} from "@angular/core";
import {
  auditTime,
  BehaviorSubject,
  distinctUntilChanged,
  Subject,
} from "rxjs";
import { binarySearchByPrevHeight } from "src/standard/cdk/binarySearchByPrevHeight";
import invariant from "tiny-invariant";

const scrollIdleMs = 100;
const scrollLingeringMs = 1_600;

export type GetItemSize<TItem> = true | number | ((item: TItem) => number);

type ScrollState = "busy" | "idle" | "lingering";
type ScrollStateBuffers = Record<ScrollState, number>;

/** Virtual scrolling strategy for lists with items of unknown or dynamic size. */
export class PerItemSizeVirtualScrollStrategy<TItem>
  implements VirtualScrollStrategy
{
  private currentIndex = new BehaviorSubject<number>(0);
  public scrolledIndexChange = this.currentIndex.pipe(distinctUntilChanged());

  private renderTrigger = new Subject<ScrollState>();

  /** The attached viewport. */
  private readonly _viewport = signal<CdkVirtualScrollViewport | null>(null);

  /** The virtual configuration */
  private readonly _cdkVirtualForOf = signal<CdkVirtualForOf<TItem> | null>(
    null,
  );

  private _lastScrollOffset = 0;
  private _lastScrollDirection: "up" | "down" = "down";
  private _buffers: ScrollStateBuffers;

  private readonly _getItemSize = signal<GetItemSize<TItem> | undefined>(
    undefined,
  );
  private readonly _items = signal<readonly TItem[] | undefined>(undefined);
  private readonly itemData = computed<
    | {
        itemSizes: { prevHeight: number; height: number }[];
        fixedItemSize: number;
        totalSize: number;
      }
    | {
        itemSizes: { prevHeight: number; height: number }[];
        fixedItemSize: undefined;
        totalSize: number;
      }
    | undefined
  >(() => {
    const items = this._items();
    if (!items) {
      return undefined;
    }

    const getItemSize = this._getItemSize();
    if (!getItemSize) {
      return undefined;
    }

    const itemSizes = new Array<{ prevHeight: number; height: number }>(
      items.length,
    );
    let prevHeight = 0;
    const getItemSizeType = typeof getItemSize;
    let hasFixedItemSize = typeof getItemSize === "number";
    for (let i = 0; i < items.length; i++) {
      const x = items[i] as TItem;
      let height: number;

      // If set, prefer to read the signal value
      if (
        x &&
        typeof x === "object" &&
        "itemSize" in x &&
        isSignal(x.itemSize)
      ) {
        height = x.itemSize() as number;

        if (hasFixedItemSize && height !== (getItemSize as number)) {
          // If the item size is not the same as the fixed item size, we need to
          // set fixedItemSize to false. This is because we can't have a mix of
          // fixed and non-fixed item sizes.
          hasFixedItemSize = false;
        }
      } else if (getItemSizeType === "number") {
        // If it's a hardcoded number, use that
        height = getItemSize as number;
      } else {
        // Otherwise, there has to be a custom getter
        invariant(typeof getItemSize === "function");
        height = getItemSize(x);
      }

      itemSizes[i] = {
        prevHeight,
        height,
      };

      prevHeight += height;
    }

    const totalSize = prevHeight;

    if (hasFixedItemSize) {
      invariant(typeof getItemSize === "number");
      return {
        itemSizes,
        fixedItemSize: getItemSize,
        totalSize,
      };
    }

    return { itemSizes, fixedItemSize: undefined, totalSize };
  });
  readonly itemSizes = computed(() => this.itemData()?.itemSizes);
  readonly fixedItemSize = computed(() => this.itemData()?.fixedItemSize);
  readonly totalSize = computed(() => this.itemData()?.totalSize);

  private syncTotalSize = effect(() => {
    const viewport = this._viewport();
    if (!viewport) {
      return;
    }

    const totalSize = this.totalSize();
    if (totalSize === undefined) {
      return;
    }

    viewport.setTotalContentSize(totalSize);

    // Trigger a re-render of the content if the size has changed.
    this.onDataLengthChanged();
  });

  /**
   * @param buffers The amount of buffer rendered beyond the viewport (in pixels) when in each state.
   */
  constructor(
    buffers: ScrollStateBuffers,
    private ngZone: NgZone,
  ) {
    this._buffers = buffers;

    effect((cleanup) => {
      const sub = this.renderTrigger.pipe(auditTime(16)).subscribe((x) => {
        this._renderContentForCurrentOffset(x);
      });

      cleanup(() => {
        sub.unsubscribe();
      });
    });

    // We want to handle virtual for of changes
    effect((cleanup) => {
      const cdkVirtualForOf = this._cdkVirtualForOf();
      if (!cdkVirtualForOf) {
        return;
      }

      // eslint-disable-next-line
      this._items.set(cdkVirtualForOf["_data"] as any);
      const sub = cdkVirtualForOf.dataStream.subscribe((items) => {
        this._items.set(items);
      });

      cleanup(() => {
        sub.unsubscribe();
      });
    });
  }

  /**
   * Attaches this scroll strategy to a viewport.
   * @param viewport The viewport to attach this strategy to.
   */
  attach(viewport: CdkVirtualScrollViewport) {
    this._viewport.set(viewport);
    this._renderContentForCurrentOffset("busy");
  }

  /** Detaches this scroll strategy from the currently attached viewport. */
  detach() {
    this.currentIndex.complete();
    this._viewport.set(null);

    this.clearTimeouts();
  }

  private clearTimeouts() {
    this.clearIdleTimeout();
    this.clearLingeringTimeout();
  }

  private clearIdleTimeout() {
    if (this._idleTimeout) {
      clearTimeout(this._idleTimeout);
      this._idleTimeout = undefined;
    }
  }

  private clearLingeringTimeout() {
    if (this._lingeringTimeout) {
      clearTimeout(this._lingeringTimeout);
      this._lingeringTimeout = undefined;
    }
  }

  /** @docs-private Implemented as part of VirtualScrollStrategy. */
  onContentScrolled() {
    const viewport = this._viewport();
    if (!viewport) {
      return;
    }

    const scrollOffset = viewport.measureScrollOffset();

    // Ignore no-op scroll events - only process when scroll changes
    if (scrollOffset === this._lastScrollOffset) {
      return;
    }

    // Always update direction and offset synchronously, at the start
    if (scrollOffset > this._lastScrollOffset) {
      this._lastScrollDirection = "down";
    } else if (scrollOffset < this._lastScrollOffset) {
      this._lastScrollDirection = "up";
    }
    this._lastScrollOffset = scrollOffset;

    this.triggerBusyRender();
  }

  private triggerBusyRender() {
    // Clear timeouts/cache
    this._lastComputedRange = undefined;
    this.clearIdleTimeout();
    this.clearLingeringTimeout();

    // Schedule some range expansion callbacks
    this._idleTimeout = setTimeout(() => {
      this.renderTrigger.next("idle");
    }, scrollIdleMs);
    this._lingeringTimeout = setTimeout(() => {
      this.renderTrigger.next("lingering");
    }, scrollLingeringMs);

    // Trigger a busy render
    this.renderTrigger.next("busy");
  }

  /** @docs-private Implemented as part of VirtualScrollStrategy. */
  onDataLengthChanged() {
    this.triggerBusyRender();
  }

  /** @docs-private Implemented as part of VirtualScrollStrategy. */
  onContentRendered() {}

  /** @docs-private Implemented as part of VirtualScrollStrategy. */
  onRenderedOffsetChanged() {}

  /** Scroll to the offset for the given index. */
  scrollToIndex(index: number, behavior: ScrollBehavior): void {
    const viewport = this._viewport();
    if (!viewport) {
      return;
    }

    const itemSizes = this.itemSizes();
    if (!itemSizes) {
      return;
    }

    // Use prevHeight directly to avoid unnecessary iteration
    const offset = itemSizes[index]?.prevHeight ?? 0;
    viewport.scrollToOffset(offset, behavior);
  }

  /**
   * Update the buffer parameters.
   * @param buffers The amount of buffer rendered beyond the viewport (in pixels).
   */
  updateBufferSize(buffers: ScrollStateBuffers) {
    this._buffers = buffers;

    this.triggerBusyRender();
  }

  updateGetItemSize(getItemSize: GetItemSize<TItem>) {
    this._getItemSize.set(getItemSize);

    this.triggerBusyRender();
  }

  updateVirtualScrollForOf(cdkVirtualForOf: CdkVirtualForOf<TItem>) {
    this._cdkVirtualForOf.set(cdkVirtualForOf);
  }

  /**
   * Recalculates the rendered content based on what should be shown at the current
   * scroll offset.
   */
  private _idleTimeout: ReturnType<typeof setTimeout> | undefined;
  private _lingeringTimeout: ReturnType<typeof setTimeout> | undefined;

  private _lastComputedRange:
    | {
        scrollOffset: number;
        firstVisibleIndex: number;
        scrollDirectionIsDown: boolean;
      }
    | undefined;

  private _renderContentForCurrentOffset(state: ScrollState) {
    this.ngZone.runOutsideAngular(() => {
      const viewport = this._viewport();
      if (!viewport) {
        return;
      }

      const itemData = this.itemData();
      if (!itemData) {
        return;
      }

      const { itemSizes, fixedItemSize } = itemData;
      if (itemSizes.length === 0) {
        viewport.setRenderedRange({ start: 0, end: 0 });
        viewport.setRenderedContentOffset(0);
        return;
      }

      const viewportSize = viewport.getViewportSize();
      if (viewportSize === 0) {
        return;
      }

      // If we're not busy, we're not scrolling
      // and we can reuse some of the previous calculations
      if (state === "busy" || !this._lastComputedRange) {
        const scrollOffset = viewport.measureScrollOffset();

        let firstVisibleIndex: number;

        if (fixedItemSize) {
          firstVisibleIndex = Math.floor(scrollOffset / fixedItemSize);
        } else {
          // --- Binary search for firstVisibleIndex ---
          // Find the index where prevHeight >= scrollOffset, then adjust for floor behavior
          const ceilIndex = binarySearchByPrevHeight(
            itemSizes,
            0,
            itemSizes.length - 1,
            scrollOffset,
          );
          // If we found an exact match or we're at the first item, use that index
          // Otherwise, use the previous item (floor behavior)
          if (
            ceilIndex === 0 ||
            itemSizes[ceilIndex]?.prevHeight === scrollOffset
          ) {
            firstVisibleIndex = ceilIndex;
          } else {
            firstVisibleIndex = ceilIndex - 1;
          }
        }

        const closePx = this._buffers.idle * 0.19;
        const isCloseToTop = scrollOffset - closePx <= 0;
        const isCloseToBottom =
          scrollOffset + viewportSize + closePx >= itemData.totalSize;
        const lastScrollDirection = isCloseToTop
          ? "down"
          : isCloseToBottom
            ? "up"
            : this._lastScrollDirection;
        const scrollDirectionIsDown = lastScrollDirection === "down";

        // Update the cached
        this._lastComputedRange = {
          scrollOffset,
          firstVisibleIndex,
          scrollDirectionIsDown,
        };
      }

      const { firstVisibleIndex, scrollOffset, scrollDirectionIsDown } =
        this._lastComputedRange;

      // If it's in the same direction, we use 80% of the buffer
      // If it's in the opposite direction, we use 20% of the buffer
      const bufferPx = this._buffers[state];
      const beforeBufferPx = scrollDirectionIsDown
        ? Math.floor(bufferPx * 0.2)
        : Math.floor(bufferPx * 0.8);
      const afterBufferPx = bufferPx - beforeBufferPx;

      let startIndex: number;
      let endIndex: number;
      if (fixedItemSize) {
        // Since the item size is fixed, we can calculate the start and end index
        // directly based on the scroll offset.
        startIndex = Math.max(
          0,
          Math.floor((scrollOffset - beforeBufferPx) / fixedItemSize),
        );
        endIndex = Math.ceil(
          (scrollOffset + viewportSize + afterBufferPx) / fixedItemSize,
        );
      } else {
        const firstVisiblePrevHeight =
          itemSizes[firstVisibleIndex]?.prevHeight ?? 0;
        // --- Calculate startIndex (before buffer) ---
        const minStartHeight = Math.max(
          0,
          firstVisiblePrevHeight - beforeBufferPx,
        );
        startIndex = binarySearchByPrevHeight(
          itemSizes,
          0,
          firstVisibleIndex,
          minStartHeight,
        );

        // --- Calculate endIndex (viewport + after buffer) ---
        const maxEndHeight =
          firstVisiblePrevHeight + viewportSize + afterBufferPx;
        endIndex = binarySearchByPrevHeight(
          itemSizes,
          firstVisibleIndex,
          itemSizes.length - 1,
          maxEndHeight,
        );
      }

      // NOTE: we want to avoid processing here if viewport range is within the rendered range
      // eg scrolling enough that we already have rendered the content in the buffer
      const currentRenderedRange = viewport.getRenderedRange();

      // Skip redundant renders if the new range is fully within the current rendered range
      if (
        this.currentIndex.value === firstVisibleIndex &&
        startIndex === currentRenderedRange.start &&
        endIndex + 1 === currentRenderedRange.end
      ) {
        return;
      }

      // Only update if the range has changed
      this.currentIndex.next(firstVisibleIndex);

      const renderedContentOffset = itemSizes[startIndex]?.prevHeight ?? 0;

      // end is exclusive, so make sure the last index is included
      const range = { start: startIndex, end: endIndex + 1 };
      viewport.setRenderedRange(range);
      viewport.setRenderedContentOffset(renderedContentOffset);
    });
  }
}

/**
 * Provider factory for `PerItemSizeVirtualScrollStrategy` that simply extracts the already created
 * `PerItemSizeVirtualScrollStrategy` from the given directive.
 * @param x The instance of `CdkPerItemSizeVirtualScroll` to extract the
 *     `PerItemSizeVirtualScrollStrategy` from.
 */
export function _perItemSizeVirtualScrollStrategyFactory<TItem>(
  x: CdkPerItemSizeVirtualScroll<TItem>,
) {
  return x._scrollStrategy;
}

/** A virtual scroll strategy that supports unknown or dynamic size items. */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: "cdk-virtual-scroll-viewport[getItemSize]",
  providers: [
    {
      provide: VIRTUAL_SCROLL_STRATEGY,
      useFactory: _perItemSizeVirtualScrollStrategyFactory,
      deps: [forwardRef(() => CdkPerItemSizeVirtualScroll)],
    },
  ],
})
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export class CdkPerItemSizeVirtualScroll<TItem> implements AfterViewInit {
  readonly ngZone = inject(NgZone);

  constructor() {
    effect(() => {
      this._scrollStrategy.updateBufferSize(this.buffers());
    });

    afterRenderEffect(() => {
      if (!this.isViewInitialized()) {
        return;
      }

      this._scrollStrategy.updateVirtualScrollForOf(this.cdkVirtualForOf());
    });

    afterRenderEffect(() => {
      if (!this.isViewInitialized()) {
        return;
      }

      this._scrollStrategy.updateGetItemSize(this.getItemSize());
    });
  }

  /**
   * The amount of buffer rendered beyond the viewport (in pixels) when busy.
   *
   * @default 0
   */
  readonly busyBufferPx = input<number>(0);

  /**
   * The amount of buffer rendered beyond the viewport (in pixels) when idle.
   *
   * @default 400
   */
  readonly idleBufferPx = input<number>(400);

  /**
   * The amount of buffer rendered beyond the viewport (in pixels) when idle for a while.
   *
   * @default 1200
   */
  readonly lingeringBufferPx = input<number>(1200);

  readonly buffers = computed<ScrollStateBuffers>(() => ({
    busy: this.busyBufferPx(),
    idle: this.idleBufferPx(),
    lingering: this.lingeringBufferPx(),
  }));

  readonly getItemSize = input.required<GetItemSize<TItem>>();

  /**
   * The `CdkVirtualForOf` instance that the virtual scroll is connected to.
   */
  readonly cdkVirtualForOf =
    contentChild.required<CdkVirtualForOf<TItem>>(CdkVirtualForOf);

  /** The scroll strategy used by this directive. */
  readonly _scrollStrategy = new PerItemSizeVirtualScrollStrategy<TItem>(
    this.buffers(),
    this.ngZone,
  );

  readonly isViewInitialized = signal<boolean>(false);

  ngAfterViewInit(): void {
    this.isViewInitialized.set(true);
  }
}

@NgModule({
  imports: [CdkPerItemSizeVirtualScroll],
  exports: [CdkPerItemSizeVirtualScroll],
})
export class CdkPerItemSizeVirtualScrollModule {}
